import React, { useEffect, useState } from 'react';
import { OrganizationLevelType } from '../../../enums';
import { ISkillsBreakdownWidgetProps, SkillsBreakdownSkill, SortbyMenuEnum } from './skills-breakdown.models';
import { PagePath } from '../../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../../overmind';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { SkillScoresSearchParams } from '../../../models/overmind/search-params';
import { SkillScoresRequest, SkillScoresSkillCategoryResponse, SkillScoresSkillResponse } from '../../../models/overmind/analytics';
import { ResponseGroup } from '../../../enums/analytics';
import { Pager, SkillLevel, SkillPercentage, scorePercentageRAGColour, scoreLevelRAGColour, useMatchScreenWidth, DropdownSelect, DropdownSelectOption } from '@keplerco/core';
import styles from './skills-breakdown.module.css';
import classNames from 'classnames';
import KeplerNavlink from '../../../navigation/guards/kepler-navlink';
import { comparisonScoreTitle } from '../../../library/helpers/comparison-score-title';
import { ScoreComparison } from '../../../enums/score-comparison';
import { SkeletonOverlay } from '../../../components/general/loading-state/skeleton-overlay';
import { WidgetCard } from '../../../pages/analytics/analytics-filters/widget-card';
import { SkillScore } from '../../../components/general/skill-score/skill-score';

const SORT_BY_OPTIONS: DropdownSelectOption[] = [
  {
    value: SortbyMenuEnum.Descending,
    label: 'High to Low',
  },
  {
    value: SortbyMenuEnum.Ascending,
    label: 'Low to High',
  },
  {
    value: SortbyMenuEnum.Organic,
    label: 'Alphabetical',
  },
];
const pageSize = 10;

export function SkillsBreakdownWidget({ entity, path, description }: ISkillsBreakdownWidgetProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables, dateRange } = useAppState();

  const [loading, setLoading] = useState<boolean>(true);
  const [skillCategories, setSkillCategories] = useState<SkillScoresSkillCategoryResponse[]>([]);

  const [filters, setFilters] = useState<DropdownSelectOption[]>([]);
  const [activeFilter, setActiveFilter] = useState<DropdownSelectOption>();
  const [filteredSkills, setFilteredSkills] = useState<SkillsBreakdownSkill[]>([]);

  const [activeSortType, setActiveSortType] = useState<DropdownSelectOption>(SORT_BY_OPTIONS[0]);
  const [sortedSkills, setSortedSkills] = useState<SkillsBreakdownSkill[]>([]);
  const [activePageNumber, setActivePageNumber] = useState<number>(1);
  const [pagedSkills, setPagedSkills] = useState<SkillsBreakdownSkill[]>([]);

  const isMobile = useMatchScreenWidth('mobile');

  useEffect(() => {
    async function getData() {
      if (!entity) return;

      setLoading(true);

      const searchParams: SkillScoresSearchParams = {
        startDate: path === PagePath.dashboard ? undefined : dateRange?.from?.toJSON(),
        endDate: path === PagePath.dashboard ? undefined : dateRange?.to?.toJSON(),
        organizationLevel: entity.organizationLevel,
        companySlug: companyVariables.slug,
        departmentSlug: undefined,
        learnerSlug: undefined,
        teamSlug: undefined,
        skillSlug: undefined,
        skillSubTypeSlug: undefined,
        includeLearnerCount: false,
        scoreComparison: companyVariables.scoreComparison,
      };

      if (entity.organizationLevel === OrganizationLevelType.Department) searchParams.departmentSlug = entity.entity.slug;
      if (entity.organizationLevel === OrganizationLevelType.Team) searchParams.teamSlug = entity.entity.slug;
      if (entity.organizationLevel === OrganizationLevelType.Learner) searchParams.learnerSlug = entity.entity.slug;

      const request: SkillScoresRequest = {
        responseGroup: ResponseGroup.Skill,
        searchParams,
      };

      const tempData = await actions.analyticsGetSkillScores(request);

      const tempSkillCategories = tempData?.map(skillType => skillType.skillCategories)?.flat() ?? [];

      setSkillCategories(tempSkillCategories);

      const tempFilters: DropdownSelectOption[] =
        tempSkillCategories
          ?.filter(skillCategory => !!skillCategory.skills.length)
          .map(skillCategory => ({
            value: skillCategory.slug,
            label: skillCategory.name,
          })) ?? [];

      if (tempFilters.length > 1) {
        tempFilters.unshift({ value: 'all', label: 'All skills' });
        setFilters(tempFilters);
      } else {
        setFilters([]);
      }

      setLoading(false);
    }

    getData();
  }, [companyVariables.slug, dateRange, entity]);

  function filterSkillsBySubType(selectedFilter: DropdownSelectOption) {
    if (!skillCategories?.length) return setFilteredSkills([]);

    if (selectedFilter.value === 'all') {
      const uniqueSkillSlugs = new Set<string>();
      const tempSkills: SkillsBreakdownSkill[] = [];

      skillCategories.forEach(skillCategory => {
        skillCategory.skills?.forEach((skill: SkillScoresSkillResponse) => {
          if (!uniqueSkillSlugs.has(skill.slug)) {
            uniqueSkillSlugs.add(skill.slug);
            tempSkills.push({
              name: skill.name,
              slug: skill.slug,
              score: skill.score,
              comparisonScore: skill.comparisonScore,
              peopleCount: skill.peopleCount,
              skillSubTypeSlug: skillCategory.slug,
            });
          }
        });
      });

      setFilteredSkills(tempSkills);
      return;
    }

    const tempSkills: SkillsBreakdownSkill[] = [];
    const selectedSubTypeSlug = selectedFilter.value;
    let foundSubType: SkillScoresSkillCategoryResponse | undefined;

    for (const skillSubType of skillCategories) {
      if (skillSubType.slug === selectedSubTypeSlug) {
        foundSubType = skillSubType;
        break;
      }
    }

    if (foundSubType) {
      const foundSubTypeSlug = foundSubType.slug;
      foundSubType.skills?.forEach((skill: SkillScoresSkillResponse) => {
        tempSkills.push({
          name: skill.name,
          slug: skill.slug,
          score: skill.score,
          comparisonScore: skill.comparisonScore,
          peopleCount: skill.peopleCount,
          skillSubTypeSlug: foundSubTypeSlug,
        });
      });
    }

    setFilteredSkills(tempSkills);
  }
  // filter
  useEffect(() => {
    if (filters.length > 1) {
      setActiveFilter(filters[0]);
      filterSkillsBySubType(filters[0]);
    } else {
      const tempSkills: SkillsBreakdownSkill[] = [];

      skillCategories.forEach(skillCategory => {
        skillCategory.skills?.forEach((skill: SkillScoresSkillResponse) =>
          tempSkills.push({
            name: skill.name,
            slug: skill.slug,
            score: skill.score,
            comparisonScore: skill.comparisonScore,
            peopleCount: skill.peopleCount,
            skillSubTypeSlug: skillCategory.slug,
          })
        );
      });

      setFilteredSkills(tempSkills);
    }
  }, [filters, skillCategories]);

  function sortSkills(selectedSortType: DropdownSelectOption) {
    if (!filteredSkills?.length) return setSortedSkills([]);

    const tempSkills: SkillsBreakdownSkill[] = structuredClone(filteredSkills);

    tempSkills.sort((a, b) => {
      if (selectedSortType.value === SortbyMenuEnum.Organic) return a.name.localeCompare(b.name);
      if (selectedSortType.value === SortbyMenuEnum.Ascending) return (a.score?.percentage ?? 0) - (b.score?.percentage ?? 0);
      if (selectedSortType.value === SortbyMenuEnum.Descending) return (b.score?.percentage ?? 0) - (a.score?.percentage ?? 0);
      return 0;
    });

    setSortedSkills(tempSkills);
  }

  // sort
  useEffect(() => {
    sortSkills(SORT_BY_OPTIONS[0]);
  }, [filteredSkills]);

  function pageSkills(tempStart: number, tempEnd: number) {
    if (!sortedSkills) return setPagedSkills([]);

    let tempSkills: SkillsBreakdownSkill[] = structuredClone(sortedSkills);
    tempSkills = tempSkills.slice(tempStart, tempEnd);
    setPagedSkills(tempSkills);
  }

  // page
  useEffect(() => {
    setActivePageNumber(1);
    pageSkills(0, pageSize);
  }, [sortedSkills]);

  function handleFilterChange(filter: React.ChangeEvent<HTMLSelectElement> | undefined) {
    const selectedFilterOption = filters.find(option => filter?.target.value === option.value);
    if (!selectedFilterOption) return;

    setActiveFilter(selectedFilterOption);
    filterSkillsBySubType(selectedFilterOption);
  }

  function handleSortChange(sortType: React.ChangeEvent<HTMLSelectElement> | undefined) {
    const selectedSortOption = SORT_BY_OPTIONS.find(option => sortType?.target.value === option.value);
    if (!selectedSortOption) return;

    setActiveSortType(selectedSortOption);
    sortSkills(selectedSortOption);
  }

  function handlePageChange(pageNumber: number) {
    setActivePageNumber(pageNumber);
    const end = pageNumber * pageSize;
    const start = end - pageSize;
    pageSkills(start, end);
  }

  return (
    <SkeletonOverlay isLoading={loading} width="100%">
      <WidgetCard title="Skills breakdown" subtitle={description} showSkillLevelHelp>
        {!pagedSkills.length && !skillCategories.length ? (
          <EmptyState title="No skills found" />
        ) : (
          <React.Fragment>
            <div className={styles.container}>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', marginTop: '4px', gap: 5 }}>
                {!!filters.length && <DropdownSelect label="Skill category" options={filters} value={activeFilter?.value} onChange={handleFilterChange} />}
                <DropdownSelect label="Sort by" options={SORT_BY_OPTIONS} value={activeSortType.value} onChange={handleSortChange} />
              </div>
              <div
                className={styles.body}
                style={{
                  gridTemplateColumns: isMobile
                    ? `minmax(0px, 1fr) 90px ${path === PagePath.dashboard && companyVariables.scoreComparison !== ScoreComparison.None ? '90px' : ''}`
                    : `minmax(0px, 1fr) 145px ${path === PagePath.dashboard && companyVariables.scoreComparison !== ScoreComparison.None ? '145px' : ''}`,
                }}
              >
                <div>
                  <div className={classNames(styles.column, styles.label)}>Skill name</div>

                  {pagedSkills.map(skill => {
                    const partialPath = !!skill.skillSubTypeSlug
                      ? PagePath.analyticsSkillSubType.replace(':companySlug', companyVariables.slug!).replace(':skillSubTypeSlug', skill.skillSubTypeSlug).replace(':skillSlug', skill.slug)
                      : PagePath.analyticsSkill.replace(':companySlug', companyVariables.slug!).replace(':skillSlug', skill.slug);

                    return (
                      <div key={`${skill.skillSubTypeSlug}-${skill.slug}`} className={classNames(styles.column, styles.name)} title={skill.name}>
                        {path !== PagePath.dashboard ? (
                          <KeplerNavlink to={`${PagePath.analyticsBase}${partialPath}`} className={styles.link}>
                            {skill.name}
                          </KeplerNavlink>
                        ) : (
                          <React.Fragment>{skill.name}</React.Fragment>
                        )}
                      </div>
                    );
                  })}
                </div>

                {companyVariables.useLevels ? (
                  <div>
                    <div className={classNames(styles.column, styles.label)}>Skill score</div>

                    {pagedSkills.map(skill => (
                      <div key={`${skill.skillSubTypeSlug}-${skill.slug}`} className={styles.column}>
                        <SkillLevel
                          level={skill.score?.level ?? companyVariables.minLevel}
                          minLevel={companyVariables.minLevel}
                          maxLevel={companyVariables.maxLevel}
                          noLevel={!skill.score}
                          dotColour={path !== PagePath.dashboard ? scoreLevelRAGColour(skill.score?.level ?? 0, companyVariables.minLevel, companyVariables.maxLevel) : 'baby-blue'}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <div className={classNames(styles.column, styles.label)}>Skill percentage</div>

                    {pagedSkills.map(skill => (
                      <div key={`${skill.skillSubTypeSlug}-${skill.slug}`} className={styles.column}>
                        <SkillPercentage percentage={skill.score?.percentage ?? 0} noPercentage={!skill.score} barColour={path !== PagePath.dashboard ? scorePercentageRAGColour(skill.score?.percentage ?? 0) : 'baby-blue'} />
                      </div>
                    ))}
                  </div>
                )}

                {path === PagePath.dashboard && companyVariables.scoreComparison !== ScoreComparison.None && (
                  <div>
                    <div className={classNames(styles.column, styles.label)}>{comparisonScoreTitle(companyVariables.scoreComparison)}</div>

                    {pagedSkills.map(skill => (
                      <div key={`${skill.skillSubTypeSlug}-${skill.slug}`} className={styles.column}>
                        <SkillScore score={skill.comparisonScore} noScoreText="Not captured" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {sortedSkills.length > pageSize && <Pager activePageNumber={activePageNumber} pageCount={Math.ceil(sortedSkills.length / pageSize)} onPageChange={handlePageChange} />}
            </div>
          </React.Fragment>
        )}
      </WidgetCard>
    </SkeletonOverlay>
  );
}
